import React, { useState, useEffect } from 'react';
import { cn } from '@/utils/utils';

interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    src?: string;
    alt: string;
    fallback?: string;
    placeholder?: string;
    className?: string;
    containerClassName?: string;
    showPlaceholder?: boolean;
    onLoadingComplete?: () => void;
    onError?: () => void;
}

const Image: React.FC<ImageProps> = ({
    src,
    alt,
    fallback = '/images/placeholder.jpg',
    placeholder,
    className,
    containerClassName,
    showPlaceholder = true,
    onLoadingComplete,
    onError,
    ...props
}) => {
    const [imageSrc, setImageSrc] = useState<string>(placeholder || fallback);
    const [isLoading, setIsLoading] = useState(true);
    //const [hasError, setHasError] = useState(false);

    useEffect(() => {
        if (src) {
            setIsLoading(true);
            //setHasError(false);

            // Precargar la imagen
            const img = new window.Image();

            img.onload = () => {
                setImageSrc(src);
                setIsLoading(false);
                //setHasError(false);
                onLoadingComplete?.();
            };

            img.onerror = () => {
                setImageSrc(fallback);
                setIsLoading(false);
                //setHasError(true);
                onError?.();
            };

            img.src = src;
        } else {
            setImageSrc(fallback);
            setIsLoading(false);
        }
    }, [src, fallback, onLoadingComplete, onError]);

    return (
        <div className={cn('relative overflow-hidden', containerClassName)}>
            {/* Placeholder/Loading */}
            {isLoading && showPlaceholder && (
                <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
                    <svg
                        className="w-8 h-8 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                    </svg>
                </div>
            )}

            {/* Imagen principal */}
            <img
                src={imageSrc}
                alt={alt}
                className={cn(
                    'transition-opacity duration-300',
                    isLoading ? 'opacity-0' : 'opacity-100',
                    className
                )}
                {...props}
            />

            {/* Error overlay (opcional) */}
            {/* {hasError && (
                <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded">
                    ErrorQ
                </div>
            )} */}
        </div>
    );
};

export default Image;